/**
 * F10 — Admin venue edits review queue.
 *
 * Route: /venues/edits
 * Permission: venues:moderate
 *
 * FIFO queue of user-suggested field edits to existing venues. Filters synced
 * to URL. Clicking a row navigates to /venues/edits/:id.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  EmptyState,
  FormField,
  Select,
  Skeleton,
  Table,
  type TableColumn,
} from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import { VENUE_EDIT_STATUSES } from '@bravemove/types';
import type { AdminPageMeta, VenueEditStatus } from '@bravemove/types';
import { adminVenueApi, type VenueEditListOpts, type VenueEditListItem } from '../api/venue.js';
import { translateApiError } from '../auth/errors.js';
import { PageHeader } from '../components/PageHeader';
import { AdminPagination } from '../components/AdminPagination';

const PAGE_LIMIT = 25;

const STATUS_TONES: Record<VenueEditStatus, 'success' | 'warning' | 'neutral' | 'danger' | 'info'> =
  {
    pending: 'warning',
    applied: 'success',
    rejected: 'danger',
  };

function buildListOpts(raw: {
  status: string | null;
  venueId: string | null;
  page: number;
}): VenueEditListOpts {
  const opts: Record<string, string | number> = { perPage: PAGE_LIMIT, page: raw.page };
  // 'all' is the explicit "no status filter" sentinel — send no `status` param so
  // the backend returns every status. (An empty/absent param would default the
  // dropdown back to 'pending', so 'all' must be carried explicitly.)
  if (raw.status && raw.status !== 'all') opts.status = raw.status;
  if (raw.venueId) opts.venueId = raw.venueId;
  return opts as unknown as VenueEditListOpts;
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    );
  } catch {
    return iso;
  }
}

export function AdminVenueEditsQueuePage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Default to 'all' on first visit so every status is shown; 'all' is the
  // explicit show-everything value (sends no `status` param to the backend).
  const statusParam = searchParams.get('status') ?? 'all';
  const venueIdParam = searchParams.get('venueId') ?? '';
  const pageParam = Math.max(Number.parseInt(searchParams.get('page') ?? '1', 10) || 1, 1);

  const [rows, setRows] = useState<readonly VenueEditListItem[]>([]);
  const [pagination, setPagination] = useState<AdminPageMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchList = useCallback(
    async (opts: VenueEditListOpts) => {
      setLoading(true);
      setRows([]);
      setError(null);
      try {
        const data = await adminVenueApi.listEdits(opts);
        setRows(data.items);
        setPagination(data.pagination ?? null);
      } catch (err) {
        setError(translateApiError(err, t));
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchList(
      buildListOpts({
        status: statusParam,
        venueId: venueIdParam || null,
        page: pageParam,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusParam, venueIdParam, pageParam]);

  function setParam(key: string, value: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      next.delete('page');
      return next;
    });
  }

  function goToPage(page: number) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (page <= 1) next.delete('page');
      else next.set('page', String(page));
      return next;
    });
  }

  const statusOptions = useMemo(
    () => [
      { value: 'all', label: t('admin:venue.editStatus.all', { defaultValue: 'All statuses' }) },
      ...VENUE_EDIT_STATUSES.map((s) => ({
        value: s,
        label: t(`admin:venue.editStatus.${s}`),
      })),
    ],
    [t],
  );

  const columns = useMemo<readonly TableColumn<VenueEditListItem>[]>(
    () => [
      {
        key: 'submitted',
        header: t('admin:venue.table.submittedAt'),
        cell: (row) => (
          <span className="text-[12px] text-ink-2">{formatDate(row.createdAt, locale)}</span>
        ),
      },
      {
        key: 'venue',
        header: t('admin:venue.table.name'),
        cell: (row) => (
          <Link
            to={`/venues/${row.venueId}`}
            className="font-medium text-[13px] text-accent hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {row.venueName ?? row.venueId}
          </Link>
        ),
      },
      {
        key: 'fieldCount',
        header: t('admin:venue.table.fieldCount'),
        cell: (row) => (
          <Badge tone="neutral" size="sm">
            {row.fieldCount}
          </Badge>
        ),
      },
      {
        key: 'status',
        header: t('admin:venue.table.status'),
        cell: (row) => (
          <Badge tone={STATUS_TONES[row.status]} size="sm">
            {t(`admin:venue.editStatus.${row.status}`)}
          </Badge>
        ),
      },
      {
        key: 'submitter',
        header: t('admin:venue.table.submitter'),
        cell: (row) => (
          <span className="text-[12px] text-ink-3">
            {row.submitterEmail ??
              (row.submittedByUserId
                ? t('admin:venue.table.registeredUser', { defaultValue: 'Registered user' })
                : t('admin:venue.table.anonymous', { defaultValue: 'Anonymous' }))}
          </span>
        ),
      },
    ],
    [t, locale],
  );

  return (
    <div className="space-y-6">
      <PageHeader title={t('admin:venue.pageTitle.edits')} />

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md">
        <FormField label={t('admin:venue.filters.status')}>
          <Select
            value={statusParam}
            onChange={(e) => setParam('status', e.target.value)}
            options={statusOptions}
          />
        </FormField>
      </div>

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<UIIcon name="edit" size={36} color="currentColor" />}
          title={t('admin:venue.table.emptyEdits')}
        />
      ) : (
        <Table
          data={rows as VenueEditListItem[]}
          columns={columns as TableColumn<VenueEditListItem>[]}
          rowKey={(row) => row.id}
          onRowPress={(row) => navigate(`/venues/edits/${row.id}`)}
          empty={t('admin:venue.table.emptyEdits')}
        />
      )}

      {pagination && !loading && rows.length > 0 ? (
        <AdminPagination
          pagination={pagination}
          shownCount={rows.length}
          onChange={goToPage}
          testId="venue-edits-pagination"
        />
      ) : null}
    </div>
  );
}
